import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, 
  Trash2, 
  Save, 
  Download, 
  BarChart3,
  TrendingUp,
  DollarSign,
  Clock
} from "lucide-react";
import NavBar from "@/components/NavBar";

interface Option {
  id: string;
  name: string;
  description: string;
  costs: {
    capital: number;
    operational: number;
    maintenance: number;
  };
  benefits: {
    financial: number;
    strategic: number;
    operational: number;
  };
  risks: string[];
  timeframe: string;
  scores: {
    feasibility: number;
    affordability: number;
    sustainability: number;
    strategic_fit: number;
  };
}

export default function OptionsAppraisal() {
  const [options, setOptions] = useState<Option[]>([
    {
      id: "1",
      name: "Do Nothing (Baseline)",
      description: "Maintain current state with minimal intervention",
      costs: { capital: 0, operational: 100000, maintenance: 50000 },
      benefits: { financial: 0, strategic: 0, operational: 0 },
      risks: ["Service degradation", "Increased maintenance costs", "Compliance issues"],
      timeframe: "Ongoing",
      scores: { feasibility: 5, affordability: 5, sustainability: 2, strategic_fit: 1 }
    }
  ]);

  const [newOption, setNewOption] = useState<Partial<Option>>({
    name: "",
    description: "",
    costs: { capital: 0, operational: 0, maintenance: 0 },
    benefits: { financial: 0, strategic: 0, operational: 0 },
    risks: [],
    timeframe: "",
    scores: { feasibility: 3, affordability: 3, sustainability: 3, strategic_fit: 3 }
  });

  const addOption = () => {
    if (newOption.name && newOption.description) {
      const option: Option = {
        id: Date.now().toString(),
        name: newOption.name,
        description: newOption.description,
        costs: newOption.costs || { capital: 0, operational: 0, maintenance: 0 },
        benefits: newOption.benefits || { financial: 0, strategic: 0, operational: 0 },
        risks: newOption.risks || [],
        timeframe: newOption.timeframe || "",
        scores: newOption.scores || { feasibility: 3, affordability: 3, sustainability: 3, strategic_fit: 3 }
      };
      setOptions([...options, option]);
      setNewOption({
        name: "",
        description: "",
        costs: { capital: 0, operational: 0, maintenance: 0 },
        benefits: { financial: 0, strategic: 0, operational: 0 },
        risks: [],
        timeframe: "",
        scores: { feasibility: 3, affordability: 3, sustainability: 3, strategic_fit: 3 }
      });
    }
  };

  const removeOption = (id: string) => {
    setOptions(options.filter(option => option.id !== id));
  };

  const calculateTotalScore = (option: Option) => {
    const { feasibility, affordability, sustainability, strategic_fit } = option.scores;
    return (feasibility + affordability + sustainability + strategic_fit) / 4;
  };

  const getBestOption = () => {
    return options.reduce((best, current) => 
      calculateTotalScore(current) > calculateTotalScore(best) ? current : best
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Options Appraisal</h1>
            <p className="text-muted-foreground">
              Systematic evaluation of alternative approaches to meet business objectives
            </p>
          </div>

          <Tabs defaultValue="appraisal" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="appraisal">Options Appraisal</TabsTrigger>
              <TabsTrigger value="comparison">Comparison Matrix</TabsTrigger>
              <TabsTrigger value="analysis">Analysis & Reports</TabsTrigger>
            </TabsList>

            <TabsContent value="appraisal" className="space-y-6">
              {/* Add New Option */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Add New Option
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Option name"
                      value={newOption.name || ""}
                      onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                    />
                    <Input
                      placeholder="Timeframe"
                      value={newOption.timeframe || ""}
                      onChange={(e) => setNewOption({ ...newOption, timeframe: e.target.value })}
                    />
                  </div>
                  <Textarea
                    placeholder="Option description"
                    value={newOption.description || ""}
                    onChange={(e) => setNewOption({ ...newOption, description: e.target.value })}
                  />
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Capital Costs (£)</label>
                      <Input
                        type="number"
                        value={newOption.costs?.capital || 0}
                        onChange={(e) => setNewOption({
                          ...newOption,
                          costs: { ...newOption.costs!, capital: Number(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Operational Costs (£)</label>
                      <Input
                        type="number"
                        value={newOption.costs?.operational || 0}
                        onChange={(e) => setNewOption({
                          ...newOption,
                          costs: { ...newOption.costs!, operational: Number(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Maintenance Costs (£)</label>
                      <Input
                        type="number"
                        value={newOption.costs?.maintenance || 0}
                        onChange={(e) => setNewOption({
                          ...newOption,
                          costs: { ...newOption.costs!, maintenance: Number(e.target.value) }
                        })}
                      />
                    </div>
                  </div>
                  <Button onClick={addOption} disabled={!newOption.name || !newOption.description}>
                    Add Option
                  </Button>
                </CardContent>
              </Card>

              {/* Existing Options */}
              <div className="space-y-4">
                {options.map((option) => (
                  <Card key={option.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {option.name}
                            <Badge variant="outline">{option.timeframe}</Badge>
                          </CardTitle>
                          <CardDescription>{option.description}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">
                            Score: {calculateTotalScore(option).toFixed(1)}/5
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeOption(option.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold mb-2 flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            Costs
                          </h4>
                          <div className="space-y-1 text-sm">
                            <div>Capital: £{option.costs.capital.toLocaleString()}</div>
                            <div>Operational: £{option.costs.operational.toLocaleString()}</div>
                            <div>Maintenance: £{option.costs.maintenance.toLocaleString()}</div>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4" />
                            Assessment Scores
                          </h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>Feasibility: {option.scores.feasibility}/5</div>
                            <div>Affordability: {option.scores.affordability}/5</div>
                            <div>Sustainability: {option.scores.sustainability}/5</div>
                            <div>Strategic Fit: {option.scores.strategic_fit}/5</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="comparison" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Options Comparison Matrix
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-border">
                      <thead>
                        <tr className="bg-muted">
                          <th className="border border-border p-2 text-left">Option</th>
                          <th className="border border-border p-2 text-center">Total Cost</th>
                          <th className="border border-border p-2 text-center">Feasibility</th>
                          <th className="border border-border p-2 text-center">Affordability</th>
                          <th className="border border-border p-2 text-center">Sustainability</th>
                          <th className="border border-border p-2 text-center">Strategic Fit</th>
                          <th className="border border-border p-2 text-center">Overall Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {options.map((option) => {
                          const totalCost = option.costs.capital + option.costs.operational + option.costs.maintenance;
                          const isRecommended = option.id === getBestOption().id;
                          return (
                            <tr key={option.id} className={isRecommended ? "bg-green-50 dark:bg-green-950" : ""}>
                              <td className="border border-border p-2">
                                <div className="flex items-center gap-2">
                                  {option.name}
                                  {isRecommended && <Badge className="bg-green-600">Recommended</Badge>}
                                </div>
                              </td>
                              <td className="border border-border p-2 text-center">£{totalCost.toLocaleString()}</td>
                              <td className="border border-border p-2 text-center">{option.scores.feasibility}/5</td>
                              <td className="border border-border p-2 text-center">{option.scores.affordability}/5</td>
                              <td className="border border-border p-2 text-center">{option.scores.sustainability}/5</td>
                              <td className="border border-border p-2 text-center">{option.scores.strategic_fit}/5</td>
                              <td className="border border-border p-2 text-center font-semibold">
                                {calculateTotalScore(option).toFixed(1)}/5
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="analysis" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Recommended Option</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {options.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="font-semibold text-lg">{getBestOption().name}</h3>
                        <p className="text-muted-foreground">{getBestOption().description}</p>
                        <Badge className="bg-green-600">
                          Score: {calculateTotalScore(getBestOption()).toFixed(1)}/5
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Export Options</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button className="w-full" variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Export to Excel
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Save className="h-4 w-4 mr-2" />
                      Save as Template
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
